import { keyframes } from '@emotion/react';
import { css } from '@recruit-robin/robin-theme/web-styles';

import { af } from '../theme';

/**
 * The scooter's width. Load-bearing: both the scooter and the trail are inset by half of
 * it, which is what keeps the trail's leading edge on the scooter's centre.
 */
export const RIDER_WIDTH = 26;

/**
 * Geometry rule (§7). The trail's leading edge is the scooter's centre at every value,
 * so 0% is flush left and 100% flush right. Inset one and not the other and they drift
 * up to 13px apart.
 */
export const riderLeft = (pct: number) => `calc(${pct}% - ${((RIDER_WIDTH * pct) / 100).toFixed(2)}px)`;
export const fillWidth = (pct: number) =>
  `calc(${pct}% - ${((RIDER_WIDTH * pct) / 100 - RIDER_WIDTH / 2).toFixed(2)}px)`;

/**
 * Rounded dashes on a 12px period. A hard-stop gradient cannot round its ends, so the
 * tile is an inline SVG.
 *
 * `--af-trail` (#555555) is baked in as a literal because `url()` backgrounds cannot read
 * custom properties — change the token and this tile together.
 *
 * The dash is inset 0.5px from the strip's top and bottom: artwork flush to an element
 * edge gets its outer row shaved at fractional zoom, which reads as a bar cropped from
 * the bottom.
 */
const TRAIL_TILE =
  'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 12 4%27%3E%3Crect y=%270.5%27 width=%275%27 height=%273%27 rx=%271.5%27 fill=%27%23555555%27/%3E%3C/svg%3E")';

/** One dash period per cycle, so the loop is seamless. */
const fillTrim = keyframes`
  from { background-position-x: 0; }
  to   { background-position-x: -12px; }
`;

/**
 * Engine jitter: bursts of jumps with rests between them rather than a constant buzz.
 * The flat runs are the pauses; the clusters between them vary in length and height so
 * no two cycles feel alike.
 */
const ride = keyframes`
  0%   { transform: translateY(0)       rotate(0deg); }
  3%   { transform: translateY(-1px)    rotate(-0.7deg); }
  6%   { transform: translateY(0.2px)   rotate(0.1deg); }
  9%   { transform: translateY(-0.65px) rotate(-0.4deg); }
  12%  { transform: translateY(0)       rotate(0deg); }
  26%  { transform: translateY(0)       rotate(0deg); }

  29%  { transform: translateY(-1.1px)  rotate(-0.75deg); }
  32%  { transform: translateY(0.3px)   rotate(0.15deg); }
  35%  { transform: translateY(-0.8px)  rotate(-0.5deg); }
  38%  { transform: translateY(0.15px)  rotate(0.05deg); }
  41%  { transform: translateY(-0.55px) rotate(-0.35deg); }
  44%  { transform: translateY(0)       rotate(0deg); }
  62%  { transform: translateY(0)       rotate(0deg); }

  65%  { transform: translateY(-0.95px) rotate(-0.6deg); }
  68%  { transform: translateY(0.35px)  rotate(0.2deg); }
  71%  { transform: translateY(0)       rotate(0deg); }
  78%  { transform: translateY(0)       rotate(0deg); }

  81%  { transform: translateY(-1.2px)  rotate(-0.8deg); }
  84%  { transform: translateY(0.45px)  rotate(0.25deg); }
  87%  { transform: translateY(-0.7px)  rotate(-0.45deg); }
  90%  { transform: translateY(0.15px)  rotate(0.07deg); }
  93%  { transform: translateY(0)       rotate(0deg); }
  100% { transform: translateY(0)       rotate(0deg); }
`;

/** Damped landing rather than one hop: nose up on launch, overshoot, two decaying swings. */
const settle = keyframes`
  0%   { transform: translateY(0)      rotate(1.4deg); }
  22%  { transform: translateY(-1.5px) rotate(-1.9deg); }
  45%  { transform: translateY(0)      rotate(1.1deg); }
  65%  { transform: translateY(-0.5px) rotate(-0.7deg); }
  82%  { transform: translateY(0)      rotate(0.35deg); }
  100% { transform: translateY(0)      rotate(0deg); }
`;

/**
 * The same landing, for the progress bar, which has to *replay* it on every advance.
 *
 * A CSS animation only restarts when its element remounts — and remounting the scooter
 * would destroy the `left` transition it is riding on, so it would snap across the bar
 * instead of gliding. The Web Animations API restarts on demand and animates `transform`,
 * leaving the `left` transition untouched.
 *
 * Kept beside the CSS keyframes above so the two cannot drift.
 */
export const SETTLE_KEYFRAMES: Keyframe[] = [
  { transform: 'translateY(0) rotate(1.4deg)', offset: 0 },
  { transform: 'translateY(-1.5px) rotate(-1.9deg)', offset: 0.22 },
  { transform: 'translateY(0) rotate(1.1deg)', offset: 0.45 },
  { transform: 'translateY(-0.5px) rotate(-0.7deg)', offset: 0.65 },
  { transform: 'translateY(0) rotate(0.35deg)', offset: 0.82 },
  { transform: 'translateY(0) rotate(0deg)', offset: 1 },
];

export const SETTLE_TIMING: KeyframeAnimationOptions = {
  duration: 550,
  easing: 'cubic-bezier(0.32, 1.12, 0.5, 1)',
};

/** The rating that earns the backflip. Anything under it is a plain hop. */
const FLIP_RATING = 5;

/**
 * How much of the sky a hop uses: a third of it at one star, all of it at five.
 *
 * A proportion rather than a pixel value, and read at tap time, because the two obvious
 * alternatives both failed. Fixed pixels landed a five-star jump inside the paragraph above.
 * Capping to the available gap stopped the collision but flattened the scale — three, four and
 * five all hit the same ceiling and looked identical. Scaling the whole range keeps five
 * distinguishable heights on every screen *and* makes a collision impossible, including after
 * a copy change that alters the layout.
 */
const HOP_BASE_SHARE = 0.15;
const HOP_SHARE_PER_STAR = 0.17;

/** Below this a hop reads as a twitch, whatever the measurement says. */
const HOP_MIN_PX = 12;

/** The hop that answers a rating, as high as the score. `headroom` is the clear sky above. */
export const hopHeight = (headroom: number, rating: number) =>
  Math.max(HOP_MIN_PX, Math.round(headroom * (HOP_BASE_SHARE + HOP_SHARE_PER_STAR * rating)));

/**
 * The scooter's answer to a rating: it hops as high as the score, and a five earns the
 * backflip.
 *
 * Built per tap rather than stored, because the height is measured from the DOM at tap time —
 * which is also why this runs through the Web Animations API. The idle engine jitter lives on
 * the same element, and a script animation takes over from it cleanly and hands back.
 *
 * The rotation must read as linear while the hop eases, so both are one keyframe set with the
 * easing per offset: the flip is 55% through at the apex and completes on the way down. Two
 * animations cannot do this — the second `transform` would simply replace the first.
 */
export const hopKeyframes = (height: number, rating: number): Keyframe[] => {
  const spin = rating >= FLIP_RATING ? -360 : 0;

  return [
    { transform: 'translateY(0) rotate(0deg)', easing: 'cubic-bezier(0.15, 0.75, 0.35, 1)', offset: 0 },
    {
      transform: `translateY(${-height}px) rotate(${spin * 0.55}deg)`,
      easing: 'cubic-bezier(0.6, 0, 0.85, 0.4)',
      offset: 0.48,
    },
    { transform: `translateY(0) rotate(${spin}deg)`, easing: 'cubic-bezier(0.3, 0.7, 0.4, 1)', offset: 0.86 },
    // The small second bounce: the landing, not a second jump.
    { transform: `translateY(${-Math.round(height * 0.12)}px) rotate(${spin}deg)`, offset: 0.93 },
    { transform: `translateY(0) rotate(${spin}deg)`, offset: 1 },
  ];
};

/** Higher jumps hang longer. `fill: none` is what hands the element back to the jitter. */
export const hopTiming = (rating: number): KeyframeAnimationOptions => ({
  duration: 380 + rating * 70,
  fill: 'none',
});

/** Drifts in, solid by 55%. Mirrors `riderExit`. */
const riderEnter = keyframes`
  from { transform: translateX(-18px); opacity: 0; }
  to   { transform: translateX(0);     opacity: 1; }
`;

/**
 * The mirror of the entry: the same 20px of travel on the reversed curve, solid through
 * the first 45% and gone before the drift ends. It never rides across the screen.
 */
const riderExit = keyframes`
  0%   { transform: translateX(0);    opacity: 1; }
  45%  { opacity: 1; }
  100% { transform: translateX(20px); opacity: 0; }
`;

/** The trail clears before the scooter leaves. */
const trailFade = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

/** The trail draws in behind an arriving scooter. */
const fillEnter = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/** Booked arrival: one transform on the group carries scooter and trail in together. */
const roadSlide = keyframes`
  from { transform: translateX(-62%); }
  to   { transform: translateX(0); }
`;

/** The intro road drifts in rather than fading on the spot. */
const miniIn = keyframes`
  0%   { opacity: 0; transform: translateX(-20px); }
  55%  { opacity: 1; }
  100% { opacity: 1; transform: translateX(0); }
`;

/** The status badge pops after the arrival has landed. */
const badgeIn = keyframes`
  from { opacity: 0; transform: scale(0.4); }
  to   { opacity: 1; transform: scale(1); }
`;

export const roadCss = {
  track: css`
    position: relative;
    height: 4px;
    /* Transparent on purpose: there is no unfilled rail. */
    background: transparent;
    border-radius: 999px;
  `,

  fill: css`
    height: 100%;
    border-radius: 999px;
    -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 100%);
    mask-image: linear-gradient(90deg, transparent 0, #000 100%);
    background-image: ${TRAIL_TILE};
    background-size: 12px 100%;
    background-repeat: repeat-x;
    animation: ${fillTrim} 0.38s linear infinite;
    transition: width 0.5s cubic-bezier(0.22, 0.61, 0.36, 1);
  `,

  /**
   * The scooter and the trail move as one rigid group — one transform on the parent,
   * never two synchronised animations. Sync by construction cannot drift.
   */
  rider: css`
    position: absolute;
    bottom: calc(100% + 3px);
    left: 0;
    width: ${RIDER_WIDTH}px;
    height: 19px;
    color: ${af.ink};
    transition: left 0.6s cubic-bezier(0.34, 1.26, 0.48, 1);
  `,

  vehicle: css`
    display: block;
    width: ${RIDER_WIDTH}px;
    animation: ${ride} 2.6s linear infinite;

    svg {
      display: block;
      width: ${RIDER_WIDTH}px;
      height: auto;
    }
  `,

  riderEnter: css`
    animation: ${riderEnter} 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) both;
  `,

  fillEnter: css`
    animation:
      ${fillEnter} 0.3s ease 0.26s both,
      ${fillTrim} 0.38s linear infinite;
  `,

  /**
   * Both animations must be named: the shorthand replaces rather than adds, so listing
   * only the entrance would kill the looping dash flow.
   */
  leavingFill: css`
    animation: ${trailFade} 0.16s ease both;
  `,

  leavingRider: css`
    animation: ${riderExit} 0.4s cubic-bezier(0.64, 0, 0.78, 0.39) 0.08s both;
  `,

  /** Taller box gives the scooter room above the line. */
  arrival: css`
    position: relative;
    height: 58px;
    margin: 10px 0 34px;
    overflow: hidden;
  `,

  /**
   * Drops the clip once the arrival has landed.
   *
   * The clip exists for the slide-in, which starts beyond the left edge — but it also leaves the
   * scooter about 15px of sky, which is not enough to tell a one-star hop from a five-star one. By
   * the time anybody can tap a star the group is at rest, so the clip has no more work to do.
   */
  arrivalOpen: css`
    overflow: visible;
  `,

  arrivalTrack: css`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
  `,

  arrivalFill: css`
    width: 50%;
  `,

  /**
   * Both roads — the intro's and the terminal screens' — draw the scooter at double the
   * progress bar's size. Only the bar itself runs it at 26px.
   *
   * The trail's inset stays 26px either way, because it is measured from the *bar's*
   * scooter; the roads position their own scooter to land the trail on its centre.
   */
  riderLarge: css`
    width: ${RIDER_WIDTH * 2}px;
    height: 36px;
  `,

  vehicleLarge: css`
    width: ${RIDER_WIDTH * 2}px;

    svg {
      width: ${RIDER_WIDTH * 2}px;
    }
  `,

  arrivalRider: css`
    left: calc(50% - ${RIDER_WIDTH}px);
  `,

  arrivalPlay: css`
    animation: ${roadSlide} 1.05s cubic-bezier(0.22, 0.61, 0.36, 1) 0.1s both;
  `,

  arrivalPlayRider: css`
    animation: ${settle} 0.5s ease 1.05s;
  `,

  /**
   * The intro road: the same component sized into the role box, scooter flush right with
   * the trail ending on its centre. It does not clip itself — the role box does, so the
   * scooter reads as riding in and out of a window.
   */
  mini: css`
    flex: 0 0 auto;
    width: 104px;
    height: 50px;
    margin: 0;
    overflow: visible;
    position: relative;
  `,

  miniTrack: css`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
  `,

  miniFill: css`
    width: calc(100% - ${RIDER_WIDTH}px);
  `,

  miniRider: css`
    left: calc(100% - ${RIDER_WIDTH * 2}px);
  `,

  miniPlay: css`
    animation: ${miniIn} 0.75s cubic-bezier(0.16, 0.84, 0.44, 1) 0.1s both;
  `,

  badge: css`
    position: absolute;
    top: -7px;
    right: -13px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    /* Filled with the stage colour so it punches out of the scooter's silhouette. */
    background: ${af.stage};
    color: ${af.ink};
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 18px;
      height: 18px;
    }
  `,

  badgeIn: css`
    animation: ${badgeIn} 0.35s cubic-bezier(0.34, 1.3, 0.5, 1) both;
  `,

  /** On Booked the badge waits for the arrival to land before it pops. */
  badgeInDelayed: css`
    animation: ${badgeIn} 0.35s cubic-bezier(0.34, 1.3, 0.5, 1) 1.25s both;
  `,

  badgeHidden: css`
    opacity: 0;
    transform: scale(0.4);
  `,

  /** The exit powers down: engine off, trail gone. It does not crash, shake or turn red. */
  parkedVehicle: css`
    animation: none;
  `,

  parkedFill: css`
    animation: ${trailFade} 0.7s ease both;
  `,
};
